/**
 * Authentication API endpoints
 */
import axiosClient from './axiosClient';
import { User, LoginCredentials, RegisterData, TokenResponse } from '@/types';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '@/utils/constants';

export interface EmbedValidateTokenResponse extends TokenResponse {
  mode?: string;
}

export const authAPI = {
  /**
   * Register new user
   */
  register: async (data: RegisterData): Promise<User> => {
    const response = await axiosClient.post<User>('/auth/register', data);
    return response.data;
  },

  /**
   * Login user
   */
  login: async (credentials: LoginCredentials): Promise<TokenResponse> => {
    const response = await axiosClient.post<TokenResponse>('/auth/login', credentials);
    return response.data;
  },

  /**
   * Validate embed session token from parent app
   */
  validateEmbedToken: async (embedToken: string): Promise<EmbedValidateTokenResponse> => {
    const response = await axiosClient.post<EmbedValidateTokenResponse>(
      '/auth/embed/validate-token',
      { embed_token: embedToken }
    );
    return response.data;
  },

  /**
   * Get current user info
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await axiosClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await axiosClient.post<TokenResponse>(
      '/auth/refresh',
      { refresh_token: refreshToken }
    );
    return response.data;
  },

  /**
   * Logout (client-side)
   */
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Request password reset
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const frontendUrl = window.location.origin;
    const response = await axiosClient.post<{ message: string }>(
      '/auth/forgot-password',
      { email },
      { params: { frontend_url: frontendUrl } }
    );
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (email: string, token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await axiosClient.post<{ message: string }>(
      '/auth/reset-password',
      { email, token, new_password: newPassword }
    );
    return response.data;
  },
};